import { Fragment, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { PRICING_TIERS } from '../data/pricing';
import { SECTIONS, GLOSS } from '../data/nav';

/* ── Shared kit (First Light) ──
   Every page is built from these: the page opening (location line, title,
   one sentence, tick rule), section heads, the annotated log, fact lists,
   the pricing table and the where-next signposts. */

/* crumbs: [{ label, to? }] — the last one is the current page.
   `section` (a key of SECTIONS) shows the section's pages instead, for
   pages one level down from the main nav.
   `meta` is a plain location line for pages without a path. */
export function PageHead({ crumbs, section, meta, title, lede, children }) {
  return (
    <header className="page-head wrap">
      {section ? <SectionNav pages={SECTIONS[section]} /> : crumbs ? (
        <nav className="crumbs label" aria-label="You are here">
          {crumbs.map((c, i) => (
            <Fragment key={c.label}>
              {i > 0 && <span className="sep" aria-hidden="true">/</span>}
              {c.to && i < crumbs.length - 1
                ? <Link to={c.to}>{c.label}</Link>
                : <span aria-current={i === crumbs.length - 1 ? 'page' : undefined}>{c.label}</span>}
            </Fragment>
          ))}
        </nav>
      ) : meta && <p className="crumbs label">{meta}</p>}
      <h1>{title}</h1>
      {lede && <p className="lede">{lede}</p>}
      {children}
      <WatchRule />
    </header>
  );
}

/* The tick rule is a watch clock: 48 ticks, one per ship's bell (every half
   hour), taller ticks where the watch changes, lit up to the reader's own
   local time. */
const WATCHES = [
  [4, 'middle watch'], [8, 'morning watch'], [12, 'forenoon watch'], [16, 'afternoon watch'],
  [18, 'first dog watch'], [20, 'last dog watch'], [24, 'first watch'],
];

function WatchRule() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);
  const h = now.getHours(), m = now.getMinutes();
  const watch = WATCHES.find(([end]) => h < end)[1];
  const time = `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'a.m.' : 'p.m.'}`;
  return (
    <div className="watch-rule" aria-hidden="true">
      <div className="ticks" style={{ '--now': (h * 60 + m) / 1440 }} />
      <p className="ticks-time label">{time} where you are · {watch}</p>
    </div>
  );
}

export function SectionHead({ mark, id, title, children }) {
  return (
    <div className="section-head grid-12">
      <span className="section-mark" aria-hidden="true">{mark}</span>
      <h2 id={id}>{title}</h2>
      {children && <p>{children}</p>}
    </div>
  );
}

/* lines: [{ text, note?, sub?, muted? }]. A line with a note gets a cyan
   highlight and a numbered annotation beside it. */
export function Specimen({ lines, caption, label }) {
  let n = 0;
  return (
    <figure className="specimen-fig">
      <div className="specimen" role="group" aria-label={label}>
        {lines.map((l, i) => {
          if (l.note) n += 1;
          return (
            <div key={i} className={`sp-row${l.note ? ' has-note' : ''}`}>
              <p className={`sp-line${l.muted ? ' muted' : ''}`}>
                {l.note
                  ? <><span className="sp-n" aria-hidden="true">{n}</span>{l.text.match(/^\s*/)[0]}<mark>{l.text.trimStart()}</mark></>
                  : l.text}
              </p>
              {l.note && (
                <p className="sp-note">
                  <span className="sp-n" aria-hidden="true">{n}</span>
                  {l.note}
                  {l.sub && <small>{l.sub}</small>}
                </p>
              )}
            </div>
          );
        })}
      </div>
      {caption && <figcaption className="fig-cap">{caption}</figcaption>}
    </figure>
  );
}

export function Facts({ items, label }) {
  return (
    <dl className="facts" aria-label={label}>
      {items.map(f => (
        <div key={f.label}>
          <dt>{f.label}</dt>
          <dd>{f.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function RateCard() {
  return (
    <>
      <div className="rate-card" aria-label="Vigilhold pricing">
        {PRICING_TIERS.map(tier => (
          <div key={tier.id} className="rate" itemScope itemType="https://schema.org/Offer">
            <meta itemProp="name" content={tier.name} />
            <meta itemProp="price" content={tier.priceLabel === 'Custom' ? '0' : tier.priceLabel.replace('$', '')} />
            <meta itemProp="priceCurrency" content="USD" />
            <p className="rate-name">{tier.name}</p>
            <p className="rate-price">{tier.priceLabel}{tier.per && <span>/month</span>}</p>
            {!tier.features.includes(tier.caption) && <p className="rate-cap">{tier.caption}</p>}
            <ul>
              {tier.features.map(f => <li key={f}>{f}</li>)}
            </ul>
          </div>
        ))}
      </div>
      <p className="label rate-note">
        Every plan starts with a 14-day trial, no card. There is no anonymous free tier — <Link to="/trust">here's why</Link>.
      </p>
    </>
  );
}

function SectionNav({ pages }) {
  const { pathname } = useLocation();
  return (
    <nav className="section-nav label" aria-label="In this section">
      {pages.map(p => (
        <Link key={p.to} to={p.to} aria-current={pathname === p.to ? 'page' : undefined}>{p.label}</Link>
      ))}
    </nav>
  );
}

/* items: [{ label, to? | href?, ref? }] — internal links get →, external ↗.
   The first plate is the suggested next step; each plate says what's there. */
export function WhereNext({ items }) {
  return (
    <nav className="where-next" aria-label="Where next">
      <div className="wrap">
      <p className="label">Where next</p>
      <div className="plates">
        {items.map((it, i) => {
          const body = (
            <>
              {i === 0 && <span className="plate-kicker">Suggested next</span>}
              <b>{it.label}</b>
              {GLOSS[it.to || it.href] && <span className="plate-gloss">{GLOSS[it.to || it.href]}</span>}
              <span className="plate-ref">{it.ref || it.to || it.href.replace(/^https?:\/\//, '')} {it.href ? '↗' : '→'}</span>
            </>
          );
          const cls = `plate${i === 0 ? ' plate-first' : ''}`;
          return it.href
            ? <a key={it.label} className={cls} href={it.href} target="_blank" rel="noopener noreferrer">{body}</a>
            : <Link key={it.label} className={cls} to={it.to}>{body}</Link>;
        })}
      </div>
      </div>
    </nav>
  );
}
